import { NewHeaderActionsProps } from 'components/header/types';
import { Grid2Icon, RowsIcon } from 'components/icons';
import { RoutedTabsPages, useRoutedTabs } from 'components/tabs';
import { DashboardTab } from 'containers/dashboard/components/DashboardTab';
import React from 'react';

import { Pagination } from 'components/pagination';
import { JobsitesFilters } from 'containers/jobsites/jobsitesWidget/types';
import { getJobsiteFilterItems } from 'containers/utils';

import { Filter, TabConfig } from '@odin-labs/components';
import {
  StateAbbreviation,
  useGetDashboardJobsitesWithStatsCurrentSessionQuery,
} from 'apollo/generated/client-operations';

import { Container } from 'components/container';
import { NewHeader } from 'components/header/NewHeader';
import { Redirect, useLocation } from 'react-router-dom';
import { AuthContext } from 'auth';
import { ensureNonEmptyItems } from 'utils';
import { isContractorAdminPortal } from 'utils/contractorMemberUser';
import { usePageQueryParams } from 'utils/usePageQueryParams';

// note: non-standard pagination size is used here (24 vs 25) because it plays
// nice with all media breakpoints- 24 is evenly divisible by 2, 3, and 4,
// meaning no unfilled rows are ever rendered outside the final page
export const dashboardPaginationPageSize = 24;

export function DashboardContainer(): React.ReactElement {
  const { currentUser: user } = React.useContext(AuthContext);
  const location = useLocation();
  const contractorAdmin = isContractorAdminPortal(user);

  const {
    page,
    developerIds,
    cities,
    states,
    search,
    updateUrl,
    loading: isUrlLoading,
  } = usePageQueryParams();
  const statesValues = states?.length
    ? Object.values(StateAbbreviation).filter((s) => states.includes(s))
    : null;

  const {
    data,
    loading,
    refetch: reloadJobsites,
  } = useGetDashboardJobsitesWithStatsCurrentSessionQuery({
    fetchPolicy: 'no-cache',
    skip: isUrlLoading || contractorAdmin,
    notifyOnNetworkStatusChange: true,
    variables: {
      userJobsitesInput: {
        includeAllJobsites: true,
        developerIds,
        cities,
        states: statesValues,
        search,
        paginationInput: {
          limit: dashboardPaginationPageSize,
          offset: page * dashboardPaginationPageSize,
        },
      },
    },
  });

  const baseRoute = '/dashboard';
  const tabsConfig: TabConfig<any>[] = ensureNonEmptyItems([
    {
      name: `Tiles`,
      relativePath: '',
      component: DashboardTab,
      leftIcon: Grid2Icon,
    },
    {
      name: `List`,
      relativePath: '/list',
      component: DashboardTab,
      leftIcon: RowsIcon,
    },
  ]);

  const { tabs, currentTab } = useRoutedTabs({
    tabsConfig,
    baseRoute,
    search: location.search,
  });

  const headerActionsProps = React.useMemo(
    (): NewHeaderActionsProps => ({
      baseRoute,
      tabsProps: { tabs, currentTab },
      onReloadPressed: reloadJobsites,
    }),
    [baseRoute, tabs, currentTab, reloadJobsites],
  );

  const presentAsList = React.useMemo(
    () => currentTab.name === 'List',
    [currentTab],
  );

  const { filtersOptions, jobsites, jobsiteCount, pageCount } =
    React.useMemo(() => {
      const queryData = data?.getCurrentSession.user;
      const jc = queryData?.jobsites.count ?? 0;
      return {
        filtersOptions: queryData?.jobsites.filtersOptions,
        jobsites: queryData?.jobsites.edges.map(({ node }) => node) ?? [],
        jobsiteCount: jc,
        pageCount: Math.ceil(jc / dashboardPaginationPageSize),
      };
    }, [data]);

  const filterItems = React.useMemo(
    () =>
      getJobsiteFilterItems({
        filtersOptions,
        developerIds,
        cities,
        states,
        search,
      }),
    [jobsites, developerIds, search],
  );

  const onFilterChangeHandler = React.useCallback(
    (changedFilters: Partial<JobsitesFilters>): void => {
      updateUrl({ page: null, ...changedFilters });
    },
    [updateUrl],
  );

  if (contractorAdmin) {
    return <Redirect to="/workers" />;
  }

  return (
    <Container className="dashboard-container">
      <NewHeader
        title="Dashboard"
        titleInfo={jobsiteCount}
        actionsProps={headerActionsProps}
      />
      <Filter
        items={filterItems}
        loading={loading}
        firstItemOnRight="search"
        onChange={onFilterChangeHandler}
      />

      <RoutedTabsPages
        baseRoute={baseRoute}
        tabs={tabs}
        componentProps={{ jobsites, asList: presentAsList }}
      />

      <Pagination
        pageIndex={page}
        pageCount={pageCount}
        pageSize={dashboardPaginationPageSize}
        onPageChange={(_pageSize, newPageIndex) => {
          updateUrl({
            page: newPageIndex + 1,
          });
        }}
      />
    </Container>
  );
}
